import { snackbar, snackbarError } from 'reusable/mui/snackbar/snackbar'
import { apiClient, apiClientSilent } from 'api/trpc/trpc-client'
import Loader from 'reusable/mobx-helpers/Loader'
import { defaultTrialOrgName, notNull } from '@gap/utils/utils'
import { FormHandlerConfig, formHandler } from 'reusable/mobx-store-utils/form-handlers/new/form-handler'
import { formatWebsiteFieldValue } from 'reusable/utils/validation'
import RegionRouterStore from 'features/main/RegionRouterStore'
import { getTaxDetailsFromCountry } from '@gap/utils/tax/tax-utils'
import countryList, { CountryCode, CurrencyCode, countryNameFromCode, getCountryCodeBasedOnTimezone } from '@gap/utils/country/countryList'
import { getDefaultCurrencyCodeForCountry } from '@gap/utils/country/countryCurrency'
import TimeZone from '@gap/utils/country/TimeZone'
import { GapAutocompleteOption } from 'features/gap-components/autocomplete/types/GapAutocompleteTypes'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import { OrgDto } from '@gap/backend/app-services/org/models/OrgDto'
import { UserTrialSetupStepEnum } from '@gap/backend/app-services/user/models/UserTrialStepDto'
import SignUpAdvisorStore from '../../SignUpAdvisorStore'
import { srcToFile } from 'reusable/utils/file-operations'
import AuthStore from 'auth/AuthStore'
import { makeAutoObservable, runInAction } from 'mobx'
import { UserOrgWithOrgDto } from '@gap/backend/app-services/user-org/models/UserOrgWithOrgDto'
import { orgContextCache } from 'cache/orgContextCache'
import { meetingCache } from 'cache/meetingCache'
import AuthAnalytics from 'analytics/AuthAnalytics'
import { GapError } from '@trpc-endpoints/trpc/error-handler/GapError'
import AuthUtils from 'auth/shared/AuthUtils'
import { trialCache } from 'cache/trialCache'

interface AuthAdvisorSignUpCreateOrgStoreProps {
   regionRouterStore: RegionRouterStore
   signUpAdvisorStore: SignUpAdvisorStore
   authStore: AuthStore
}

export default class SignUpAdvisorTrialOrgSetupStore {
   sp
   loader = new Loader(true)
   saveLoader = new Loader(false)
   getLogoLoader = new Loader(false)
   isUploading = false
   timeZone: TimeZone
   showLogoUploadHelperText = true
   existingLogoFile: File | undefined = undefined
   isNavigating = false

   fields = {
      name: '',
      website: '',
      countryCode: '' as CountryCode,
      currencyCode: '' as CurrencyCode,
   }

   readonly countryOptions: GapAutocompleteOption[] = countryList.map(item => ({
      id: item.code,
      label: item.name
   }))

   constructor(sp: AuthAdvisorSignUpCreateOrgStoreProps) {
      this.sp = sp
      this.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone as TimeZone
      makeAutoObservable(this)
      const countryCode = getCountryCodeBasedOnTimezone(this.timeZone)
      if (countryCode) {
         this.formHandler.countryCode.setValue(countryCode)
      }
   }

   get formHandler() {
      return formHandler(this.fields, {
         name: { type: 'string' },
         website: { type: 'string', },
         countryCode: {
            type: 'string',
            onValueChange: () => this.setCurrency(this.fields.countryCode)
         },
         currencyCode: { type: 'string' },
      } satisfies FormHandlerConfig)
   }

   /**
    * Can continue if the user has entered a name, country and is not uploading a logo
    */
   get canContinue() {
      return this.fields.name.length >= 1 &&
         this.fields.countryCode.length >= 1 &&
         !this.getLogoLoader.isLoading &&
         !this.isUploading
   }

   get pageIsReady() {
      return !this.loader.isLoading
   }

   getUploadedLogoUrl = async () => {
      return this.getLogoLoader.executeLoading(async () => {
         const trialOrg = this.sp.signUpAdvisorStore.trialOrg
         if (!trialOrg) return ""

         // Return existing logo URL if navigating or if logo already exists
         if (this.isNavigating || trialOrg.logoUrl) {
            return trialOrg.logoUrl || ""
         }

         // Only fetch from API if we don't have a logo URL yet
         const result = await apiClient.org.getLogoUrl.query({
            orgId: trialOrg.id
         })

         trialOrg.logoUrl = result.url
         this.isUploading = false

         return result.url
      })
   }

   setCurrency = (countryCode: CountryCode) => {
      this.fields.currencyCode = getDefaultCurrencyCodeForCountry(countryCode)
   }

   handleExistingUserSignup = () => {
      // Assuming the user is not currently signed in, point them to signin view
      this.sp.regionRouterStore.navigate('/')
   }

   /**
    * Fetches the trial org and updates the user attributes
    */
   fetchTrialOrg = async () => {
      return this.loader.executeLoading(async () => {
         try {
            // ensure we fetch the latest user attributes
            await this.sp.authStore.fetchUserAttributes()

            // Get the trial org from store or the database
            let trialOrg = this.sp.signUpAdvisorStore.trialOrg ?? await apiClientSilent.org.getMyTrialOrg.query()

            // If the trial isnt found, let the backend handle it
            if (!trialOrg) {
               trialOrg = await apiClient.org.createTrialOrg.mutate()
            }

            await this.setTrialOrg(trialOrg)
            this.emitXeroAccountCreatedOnce()

            // ensure the fields are set in a batch and ui only updates once
            runInAction(() => {
               this.fields.name = trialOrg.name === defaultTrialOrgName ? '' : trialOrg.name
               this.fields.website = trialOrg.website ?? ''
               // Must go through the form handler: a direct field write skips onValueChange,
               // leaving currencyCode empty, which fails createTrialSchema on save
               this.formHandler.countryCode.setValue(trialOrg.countryCode ?? getCountryCodeBasedOnTimezone(this.timeZone) ?? 'NZ')
               if (trialOrg.currencyCode) {
                  this.fields.currencyCode = trialOrg.currencyCode
               }
            })

            await this.loadLogoFileIfPresent(trialOrg.logoUrl)
         } catch (error: any) {
            if (error.name === 'UserUnAuthenticatedException') {
               // User should be authenticated here. Redirect back to login page.
               // They should be able to login, and the trailOrgId should exists in their cognito user to redirect them back here
               return this.sp.regionRouterStore.navigate('/')
            }

            if (error.message === GapError.messages.auth.userDoesNotExist) {
               // User exists in cognito but not in dynamodb
               // Create the user and reload the component. This scenario is most likely triggered by verifying an account at a later stage 
               await AuthUtils.createAdvisorUser({
                  email: notNull(this.sp.authStore.cognitoAttributes.email),
                  firstName: notNull(this.sp.authStore.cognitoAttributes.firstName),
                  lastName: notNull(this.sp.authStore.cognitoAttributes.lastName),
                  jobTitle: this.sp.authStore.cognitoAttributes.jobTitle,
               })
               this.fetchTrialOrg()
            } else {
               snackbarError(error instanceof Error ? error.message : 'Failed to load trial org')
               throw error
            }
         }
      })
   }

   onFileRemove = () => {
      this.existingLogoFile = undefined
      this.showLogoUploadHelperText = true
      notNull(this.sp.signUpAdvisorStore.trialOrg).logoUrl = undefined
      this.isUploading = false
   }

   onUploadStarted = () => {
      this.isUploading = true
   }

   onFilesValidated = (files: File[]) => {
      notNull(this.sp.signUpAdvisorStore.trialOrg).logoUrl = undefined
      this.existingLogoFile = files[0]
      this.showLogoUploadHelperText = false
      this.isUploading = false
   }

   getFileUploadUrl = async (file: File) => {
      this.showLogoUploadHelperText = false
      const { url } = await apiClient.org.getLogoUploadUrl.query({
         orgId: notNull(this.sp.signUpAdvisorStore.trialOrg).id,
         fileName: file.name
      })
      notNull(this.sp.signUpAdvisorStore.trialOrg).logoUrl = url
      return url
   }

   saveOrgData = () => {
      if (this.saveLoader.isLoading || this.isNavigating) return
      formatWebsiteFieldValue(this.fields)
      // A country is always selected by this point, so the currency can always be derived.
      // Guards against any path that sets the country without the form handler's onValueChange.
      if (this.fields.countryCode && !this.fields.currencyCode) {
         this.setCurrency(this.fields.countryCode)
      }
      if (!this.formHandler.validate(OrgDto.createTrialSchema)) return

      return this.saveLoader.executeLoading(async () => {
         try {
            const updatedTrialInfo = {
               ...this.fields,
               timezone: this.timeZone,
               ...getTaxDetailsFromCountry(this.fields.countryCode)
            }

            await apiClient.org.updateTrialOrg.mutate(updatedTrialInfo)
            const trialOrg = notNull(this.sp.signUpAdvisorStore.trialOrg)

            trialOrg.name = updatedTrialInfo.name
            trialOrg.countryCode = updatedTrialInfo.countryCode
            trialOrg.website = updatedTrialInfo.website
            trialOrg.currencyCode = updatedTrialInfo.currencyCode

            if (!this.sp.authStore.hasFinishedTrialSetup) {
               await apiClient.user.ensureIAmAccepted.mutate().catch(() => undefined)

               AuthAnalytics.onOrgSetupCompleted({
                  "Org country": countryNameFromCode(this.fields.countryCode).name,
                  "Org name": this.fields.name,
                  ...this.fields.website && { "Org website": this.fields.website },
                  "Logo uploaded": !!this.existingLogoFile
               })

               await this.sp.authStore.updateUserAttributes({
                  'custom:trialStep': UserTrialSetupStepEnum.completed
               })

               AuthAnalytics.signUpCompleted()
               trialCache.registrationMethod.clear()

               meetingCache.ttvInsightsCompletedAt.set(new Date().toISOString())
               meetingCache.ttvMilestoneFired.clear()
               meetingCache.ttvDistinctMeetingIds.clear()
            }

            this.isNavigating = true
            this.sp.regionRouterStore.navigate('/', true)
         } catch (error) {
            snackbar({ title: error instanceof Error ? error.message : String(error), variant: 'error' })
         }
      })
   }

   /**
 * Sets the trial org and updates the user attributes
 * @param trialOrg - The trial org to set
 */
   private setTrialOrg = async (trialOrg: UserOrgWithOrgDto) => {
      this.sp.signUpAdvisorStore.trialOrg = trialOrg
      orgContextCache.authOrgId.set(trialOrg.id)
      await this.sp.authStore.updateUserAttributes({
         ...this.sp.authStore.hasFinishedTrialSetup ? {} : { 'custom:trialStep': UserTrialSetupStepEnum.orgSetup },
         'custom:trialOrgId': trialOrg.id
      })
   }

   private loadLogoFileIfPresent = async (url?: string) => {
      if (!url) return
      const extension = url.split('?')[0].split('.').pop()?.toLowerCase()
      if (!extension) return
      this.existingLogoFile = await srcToFile(url, `logo.${extension}`)
   }

   /** Emits `accountCreated` for a Xero user the first time they reach org setup. */
   private emitXeroAccountCreatedOnce = () => {
      const email = this.sp.authStore.cognitoAttributes.email
      if (!this.sp.authStore.isXeroUser || !email || trialCache.xeroAccountCreatedEmail.get() === email) return
      trialCache.xeroAccountCreatedEmail.set(email)
      AuthAnalytics.accountCreated({ authenticationMethod: 'Xero SSO', registrationMethod: this.sp.signUpAdvisorStore.registrationMethod })
   }
}

export const { Provider: SignUpAdvisorTrialOrgSetupStoreProvider, useProvidedStore: useSignUpAdvisorTrialOrgSetupStore } = createStoreContext(SignUpAdvisorTrialOrgSetupStore)
